/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { gnoment } from 'shared-ui/gnoment';
import {
    Icon,
    Button,
    VerticalDivider,
    Stack,
    FlexRow,
    FlexCol,
    Inline,
    Text,
} from 'sarsaparilla';

const propTypes = {
    startDate: PropTypes.instanceOf(gnoment).isRequired,
    endDate: PropTypes.instanceOf(gnoment).isRequired,
    onDatesChange: PropTypes.func.isRequired,
    handleNextAvailableClick: PropTypes.func,
    handleClearDatesClick: PropTypes.func.isRequired,
    daysToSkip: PropTypes.number,
    weeksToSkip: PropTypes.number,
};

const defaultProps = {
    onDatesChange: () => {},
    daysToSkip: null,
    weeksToSkip: null,
};

export default class AvailabilityNavigator extends React.Component {
    constructor(props) {
        super(props);
        this.navToNextDays = this.navToNextDays.bind(this);
        this.navToPrevDays = this.navToPrevDays.bind(this);
        this.skip = this.skip.bind(this);
    }

    skip(duration, temporalUnit) {
        const newStartDate = this.props.startDate.clone().add(duration, temporalUnit);
        const newEndDate = this.props.endDate.clone().add(duration, temporalUnit);
        this.props.onDatesChange({ startDate: newStartDate, endDate: newEndDate });
    }

    navToPrevDays() {
        if (this.props.daysToSkip) {
            this.skip(-this.props.daysToSkip, 'days');
        } else if (this.props.weeksToSkip) {
            this.skip(-this.props.weeksToSkip, 'weeks');
        }
    }

    navToNextDays() {
        if (this.props.daysToSkip) {
            this.skip(this.props.daysToSkip, 'days');
        } else if (this.props.weeksToSkip) {
            this.skip(this.props.weeksToSkip, 'weeks');
        }
    }

    isDisabled() {
        return gnoment().startOf('day').toJSON() === this.props.startDate.toJSON();
    }

    renderMonthYearTitle() {
        const { startDate, isMobileApp } = this.props;
        const monthYearTitle = `Starting ${startDate.format('MMM Do YYYY')}`;

        return (
            <Text size={isMobileApp ? 'lg' : 'xl'} fontWeight="semibold">
                {monthYearTitle}
            </Text>
        );
    }

    render() {
        return (
            <Stack>
                <FlexRow alignItems="center" className="mb-1 mr-0">
                    <FlexCol alignSelf="start">{this.renderMonthYearTitle()}</FlexCol>
                    <FlexCol xs="variable">
                        <Inline>
                            <Button
                                size="xs"
                                appearance="link"
                                onClick={this.props.handleClearDatesClick}
                                iconBeforeElement={
                                    <Icon iconName="close-circle-outline" />
                                }
                                screenReaderTextAfter="that were selected"
                            >
                                Clear Dates
                            </Button>
                            <VerticalDivider />

                            <Button
                                size="xs"
                                className={`ml-1 ${this.isDisabled() ? ' disable-button' : ''}`}
                                appearance="link"
                                onClick={this.navToPrevDays}
                                isDisabled={this.isDisabled()}
                                iconBeforeElement={<Icon iconName="chevron-left" />}
                                screenReaderTextBefore="View"
                            >
                                Prev{' '}
                                {this.props.daysToSkip && `${this.props.daysToSkip} Days`}{' '}
                                {this.props.weeksToSkip &&
                                    `${this.props.weeksToSkip} Weeks`}
                            </Button>
                            <VerticalDivider />

                            {this.props.handleNextAvailableClick && (
                                <>
                                    <Button
                                        size="xs"
                                        className="ml-1"
                                        appearance="link"
                                        onClick={this.props.handleNextAvailableClick}
                                        screenReaderTextBefore="Go to"
                                        screenReaderTextAfter="dates"
                                    >
                                        Next Available
                                    </Button>
                                    <VerticalDivider />
                                </>
                            )}

                            <Button
                                size="xs"
                                className="ml-1 mr-2"
                                appearance="link"
                                onClick={this.navToNextDays}
                                iconAfterElement={<Icon iconName="chevron-right" />}
                                screenReaderTextBefore="View"
                            >
                                Next{' '}
                                {this.props.daysToSkip && `${this.props.daysToSkip} Days`}{' '}
                                {this.props.weeksToSkip &&
                                    `${this.props.weeksToSkip} Weeks`}
                            </Button>
                        </Inline>
                    </FlexCol>
                </FlexRow>
            </Stack>
        );
    }
}

AvailabilityNavigator.propTypes = propTypes;
AvailabilityNavigator.defaultProps = defaultProps;
